import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogActions,
  Snackbar,
  Alert,
  Pagination,
  Stack,
} from "@mui/material";
import { Search, Add, Delete } from "@mui/icons-material";
import {
  fetchTestimonials,
  deleteTestimonial,
} from "../redux/testimonialsSlice";

const TestimonialsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const { testimonials } = useSelector((state) => state.testimonials);
  const user = useSelector((state) => state.user.currentUser);

  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || ""
  );
  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get("page")) || 1
  );
  const testimonialsPerPage = 10;

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [testimonialToDelete, setTestimonialToDelete] = useState(null);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  useEffect(() => {
    dispatch(fetchTestimonials());
  }, [dispatch]);

  useEffect(() => {
    const params = {};
    if (searchTerm) params.search = searchTerm;
    if (currentPage > 1) params.page = currentPage;
    setSearchParams(params);
  }, [searchTerm, currentPage, setSearchParams]);

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteTestimonial(testimonialToDelete)).unwrap();
      dispatch(fetchTestimonials());
      showToast("Yorum silindi", "info");
    } catch {
      showToast("Silme başarısız", "error");
    } finally {
      setConfirmDelete(false);
      setTestimonialToDelete(null);
    }
  };

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
  };

  const filtered = useMemo(() => {
    let list = [...testimonials];
    if (searchTerm) {
      list = list.filter(
        (t) =>
          t.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.content.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return list;
  }, [testimonials, searchTerm]);

  const totalPages = Math.ceil(filtered.length / testimonialsPerPage);
  const visibleTestimonials = filtered.slice(
    (currentPage - 1) * testimonialsPerPage,
    currentPage * testimonialsPerPage
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        mb={3}
      >
        <Typography variant="h5" fontWeight="bold">
          Yorumlar
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Ara..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {(user?.role === "admin" || user?.role === "editor") && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate("/admin/testimonials-editor")}
            >
              Yeni Yorum
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Table */}
      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Ad Soyad</TableCell>
              <TableCell>Ünvan</TableCell>
              <TableCell>Yorum</TableCell>
              <TableCell align="right">İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleTestimonials.map((item) => (
              <TableRow key={item._id} hover>
                <TableCell>{item._id.slice(-6)}</TableCell>
                <TableCell>{item.fullName}</TableCell>
                <TableCell>{item.designation}</TableCell>
                <TableCell>{item.content}</TableCell>
                <TableCell align="right">
                  {user?.role === "admin" && (
                    <IconButton
                      color="error"
                      onClick={() => {
                        setTestimonialToDelete(item._id);
                        setConfirmDelete(true);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {visibleTestimonials.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary">
                    Hiç yorum bulunamadı
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Pagination */}
      {totalPages > 1 && (
        <Stack alignItems="center" mt={3}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, value) => setCurrentPage(value)}
            color="primary"
            size="small"
          />
        </Stack>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Bu yorumu silmek istiyor musunuz?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>İptal</Button>
          <Button color="error" onClick={handleDeleteConfirm}>
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.show}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.type}
          onClose={() => setToast((t) => ({ ...t, show: false }))}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TestimonialsPage;
